import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  BarChart3,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Factory,
  FileText,
  Globe,
  Image,
  Languages,
  LayoutDashboard,
  LayoutTemplate,
  LogOut,
  Code2,
  MailCheck,
  Newspaper,
  Package,
  Plug,
  UserCircle,
  Receipt,
  Rss,
  Search,
  Server,
  Settings,
  Shield,
  Sparkles,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useLanguagePreference } from "@/hooks/useLanguagePreference";
import { useCompany } from "@/hooks/useCompany";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/adminUsers";
import { featureForRoute, usePackageAccess } from "@/lib/packageAccess";

const navSections = [
  {
    labelKey: "nav.overview",
    items: [{ titleKey: "nav.dashboard", icon: LayoutDashboard, path: "/dashboard" }],
  },
  {
    labelKey: "nav.content",
    items: [
      { titleKey: "nav.websites", icon: Globe, path: "/websites" },
      { titleKey: "nav.articles", icon: FileText, path: "/articles" },
      { titleKey: "nav.authors", icon: UserCheck, path: "/authors" },
      { titleKey: "nav.contentFactory", icon: Factory, path: "/content-factory" },
      { titleKey: "nav.sources", icon: Rss, path: "/sources" },
      { titleKey: "nav.aiStudio", icon: Sparkles, path: "/ai-studio" },
      { titleKey: "nav.mediaLibrary", icon: Image, path: "/media-library" },
    ],
  },
  {
    labelKey: "nav.operations",
    items: [
      { titleKey: "nav.publishedArticles", icon: Newspaper, path: "/published" },
      { titleKey: "nav.seoCenter", icon: Search, path: "/seo" },
      { titleKey: "nav.translationCenter", icon: Languages, path: "/translations" },
      { titleKey: "nav.templates", icon: LayoutTemplate, path: "/templates" },
      { titleKey: "nav.autoPublishing", icon: Zap, path: "/automation" },
    ],
  },
  {
    labelKey: "nav.system",
    items: [
      // /analytics is a placeholder that's shown to every user with no
      // feature gate; keep it out of the sidebar until the page is real.
      { titleKey: "nav.domainsHosting", icon: Server, path: "/domains" },
      { titleKey: "nav.teams", icon: Users, path: "/teams" },
      { titleKey: "nav.developers", icon: Code2, path: "/developpeurs" },
      { titleKey: "nav.billing", icon: CreditCard, path: "/billing/subscription" },
      { titleKey: "nav.credits", icon: CreditCard, path: "/billing/credits" },
      { titleKey: "nav.invoiceTemplates", icon: Receipt, path: "/admin/invoice-templates" },
      { titleKey: "nav.emailTemplates", icon: MailCheck, path: "/admin/email-templates" },
      { titleKey: "nav.orgAiDefaults", icon: Sparkles, path: "/org/settings/ai-defaults" },
      { titleKey: "nav.profile", icon: UserCircle, path: "/profile" },
      { titleKey: "nav.settings", icon: Settings, path: "/settings" },
    ],
  },
  {
    labelKey: "nav.adminConsole",
    items: [
      { titleKey: "nav.integrations", icon: Plug, path: "/integrations" },
      { titleKey: "nav.companies", icon: Building2, path: "/companies" },
      { titleKey: "nav.adminConsole", icon: Shield, path: "/admin/billing" },
      { titleKey: "nav.adminPackages", icon: Package, path: "/admin/packages" },
      { titleKey: "nav.services", icon: Server, path: "/admin/services" },
      { titleKey: "nav.subscriptionsAdmin", icon: Users, path: "/admin/subscriptions" },
      { titleKey: "nav.invoices", icon: Receipt, path: "/admin/invoices" },
      { titleKey: "nav.invoiceTemplates", icon: Receipt, path: "/admin/invoice-templates" },
      { titleKey: "nav.emailTemplates", icon: MailCheck, path: "/admin/email-templates" },
      { titleKey: "nav.emailLogs", icon: MailCheck, path: "/admin/email-logs" },
      { titleKey: "nav.rolesPermissions", icon: Shield, path: "/admin/roles-permissions" },
      { titleKey: "nav.auditLogs", icon: ClipboardList, path: "/admin/audit-logs" },
      { titleKey: "nav.qaReview", icon: ClipboardCheck, path: "/admin/qa-review" },
    ],
  },
];

const isPathActive = (pathname: string, path: string) =>
  pathname === path || (path !== "/dashboard" && pathname.startsWith(`${path}/`));

const tourIdForPath = (path: string) => `tour-nav-${path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "home"}`;

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const { t, i18n } = useTranslation();
  const { changeLanguage } = useLanguagePreference();
  const { memberships, currentCompany, currentCompanyId, role, isPlatformOwner, setCurrentCompanyId } = useCompany();
  const [companySearch, setCompanySearch] = useState("");
  const access = usePackageAccess();
  const displayName = (user?.user_metadata?.full_name as string | undefined) || user?.email || t("public.account");
  const avatarUrl = user?.user_metadata?.avatar_url as string | undefined;
  const filteredMemberships = useMemo(() => {
    const term = companySearch.trim().toLowerCase();
    if (!term) return memberships;
    return memberships.filter((membership) =>
      membership.companies.name.toLowerCase().includes(term) ||
      membership.companies.slug.toLowerCase().includes(term)
    );
  }, [companySearch, memberships]);

  const selectCompany = (companyId: string) => {
    setCurrentCompanyId(companyId);
    if (isPlatformOwner) navigate("/websites");
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border px-3 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex w-full items-center gap-3 overflow-hidden rounded-lg px-2 py-1.5 text-left outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Company switcher"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
                <p className="truncate text-sm font-semibold tracking-tight text-sidebar-foreground">
                  {currentCompany?.name ?? "Delapresse"}
                </p>
                {role && <p className="truncate text-xs capitalize text-sidebar-foreground/60">{role}</p>}
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-sidebar-foreground/45 group-data-[collapsible=icon]:hidden" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-72 p-2">
            <DropdownMenuLabel className="px-1.5 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              {isPlatformOwner ? "Switch company" : "Workspace"}
            </DropdownMenuLabel>
            {isPlatformOwner && (
              <input
                value={companySearch}
                onChange={(event) => setCompanySearch(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                placeholder="Search companies"
                className="mb-2 h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            )}
            <div className="max-h-80 overflow-y-auto">
              {filteredMemberships.length === 0 && (
                <div className="px-2 py-3 text-sm text-muted-foreground">No companies found</div>
              )}
              {filteredMemberships.map((membership) => (
                <DropdownMenuItem
                  key={membership.company_id}
                  onClick={() => selectCompany(membership.company_id)}
                  className={`gap-2 rounded-md py-2 ${membership.company_id === currentCompanyId ? "bg-accent font-semibold" : ""}`}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{membership.companies.name}</span>
                  <span className="ml-2 text-[11px] text-muted-foreground">{membership.company_id === currentCompanyId ? "Active" : isPlatformOwner ? "Open" : membership.role}</span>
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarHeader>

      <SidebarContent className="gap-0 px-2 py-3">
        {navSections.map((section) => {
          const visibleItems = section.items.filter((item) => {
            if (item.path === "/companies") return isPlatformOwner;
            if (item.path === "/admin/invoice-templates" || item.path === "/admin/email-templates") {
              return isPlatformOwner || ["owner", "admin"].includes(role ?? "");
            }
            if (item.path.startsWith("/billing")) return isPlatformOwner || ["owner", "admin"].includes(role ?? "");
            if (item.path.startsWith("/admin")) return isPlatformOwner;
            if (item.path === "/integrations") return isPlatformOwner || ["owner", "admin"].includes(role ?? "");
            const feature = featureForRoute(item.path);
            return !feature || access.can(feature);
          });
          if (visibleItems.length === 0) return null;
          return (
          <SidebarGroup key={section.labelKey} className="py-0">
            <SidebarGroupLabel>{t(section.labelKey)}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleItems.map((item) => {
                  const active = isPathActive(location.pathname, item.path);
                  const title = item.titleKey.startsWith("nav.") ? t(item.titleKey) : item.titleKey;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton asChild isActive={active} tooltip={title}>
                        <NavLink to={item.path} end={item.path === "/dashboard"} data-tour-id={tourIdForPath(item.path)}>
                          <item.icon />
                          <span>{title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring">
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarImage src={avatarUrl} />
                <AvatarFallback className="text-[11px]">{getInitials(displayName)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
                <p className="truncate text-sm font-medium text-sidebar-foreground">{displayName}</p>
                <p className="truncate text-xs text-sidebar-foreground/55">{i18n.language === "fr" ? "Francais" : "English"}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-sidebar-foreground/45 group-data-[collapsible=icon]:hidden" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-64 p-2">
            <DropdownMenuLabel className="px-2 py-1.5">
              <span className="block truncate text-sm font-semibold">{displayName}</span>
              {user?.email && <span className="block truncate text-xs font-normal text-muted-foreground">{user.email}</span>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")} className="gap-2 rounded-md">
              <UserCircle className="h-4 w-4" />
              <span>{t("nav.profile")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void changeLanguage(i18n.language === "fr" ? "en" : "fr")}
              className="gap-2 rounded-md"
            >
              <Languages className="h-4 w-4" />
              <span>{i18n.language === "fr" ? "Switch to English" : "Passer en francais"}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={toggleSidebar} className="gap-2 rounded-md">
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              <span>{collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="gap-2 rounded-md text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4" />
              <span>{t("nav.signOut")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

